import Styles from './Features.module.scss'
import { PaymentPlan, PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { Check } from 'almanac-ui/icons/16/Check'
import { BillingFrequency } from 'components/Paywall/UpgradePlanModal/UpgradePlanModal'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import {
  MONTHLY_ADDITIONAL_SEAT_PRICE,
  YEARLY_ADDITIONAL_SEAT_PRICE,
} from 'components/Growth/InviteTeamModal/components/PriceIncreaseMessage/PriceIncreaseMessage'
import { kFormatter } from 'utils'
import { Tooltip } from 'components/Tooltip/Tooltip'

interface Props {
  plan: PaymentPlan
  billingFrequency: BillingFrequency
}

export const currencyFormatter = (cents?: number) => {
  if (!cents) return 'Free'

  return `$${(cents / 100).toFixed(cents % 100 > 0 ? 2 : 0)}`
}

export const Features = ({ plan, billingFrequency }: Props) => {
  const additionalSeatPrice =
    billingFrequency === 'month' ? MONTHLY_ADDITIONAL_SEAT_PRICE : YEARLY_ADDITIONAL_SEAT_PRICE

  const priceKey = `${billingFrequency}ly_price` as 'monthly_price' | 'yearly_price'
  const usageBasedPricingEnabled = useFlipperFlag('usage_based_pricing')

  return (
    <div className={Styles.features}>
      {plan.key === PaymentPlanKey.BlazeCreator && (
        <>
          <p className={Styles.featureDetails}>
            <span>Creator plan includes:</span>
          </p>
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>1 seat</p>
          </div>
          {usageBasedPricingEnabled && (
            <div className={Styles.featureDetails}>
              <Check />
              <p className={Styles.featureText}>
                {kFormatter(plan.additionals?.ai_words?.plan_limit)} AI Generated Words, <br />
                then {currencyFormatter(plan.additionals?.ai_words?.[priceKey])} per{' '}
                {kFormatter(plan.additionals?.ai_words?.amount_per_unit)} words
              </p>
            </div>
          )}
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>3 Brand Voices</p>
          </div>
          {!usageBasedPricingEnabled && (
            <div className={Styles.featureDetails}>
              <Check />
              <p className={Styles.featureText}>Unlimited AI Generation</p>
            </div>
          )}
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>
              {usageBasedPricingEnabled ? (
                <>
                  {kFormatter(plan.additionals?.plagiarism_checks?.plan_limit)} Plagiarism Checks, <br />
                  then {currencyFormatter(plan.additionals?.plagiarism_checks?.[priceKey])} per check
                </>
              ) : (
                'Unlimited Plagiarism Checks'
              )}
            </p>
          </div>
          <div className={Styles.featureDetails}>
            <Check />
            <div>
              <p className={Styles.featureText}>Unlimited guest access to docs &</p>
              <p className={Styles.featureText}>projects</p>
            </div>
          </div>
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>Unlimited Multi Channel Campaigns</p>
          </div>
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>Unlimited Docs & Projects</p>
          </div>
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>Unlimited SEO Checks</p>
          </div>
        </>
      )}
      {plan.key === PaymentPlanKey.BlazePro && (
        <>
          <p className={Styles.featureDetails}>
            <span>
              Everything in <strong>Creator</strong>, plus:
            </span>
          </p>
          {usageBasedPricingEnabled && (
            <div className={Styles.featureDetails}>
              <Check />
              <p className={Styles.featureText}>
                {kFormatter(plan.additionals?.ai_words?.plan_limit)} AI Generated Words, <br />
                then {currencyFormatter(plan.additionals?.ai_words?.[priceKey])} per{' '}
                {kFormatter(plan.additionals?.ai_words?.amount_per_unit)} words
              </p>
            </div>
          )}
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>3 Brand Voices</p>
          </div>
          {usageBasedPricingEnabled && (
            <div className={Styles.featureDetails}>
              <Check />
              <p className={Styles.featureText}>
                {kFormatter(plan.additionals?.plagiarism_checks?.plan_limit)} Plagiarism Checks, <br />
                then {currencyFormatter(plan.additionals?.plagiarism_checks?.[priceKey])} per check
              </p>
            </div>
          )}
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>Multiple post wizards</p>
          </div>
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>Priority Support</p>
          </div>
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>Priority AI Processing</p>
          </div>
        </>
      )}
      {plan.key === PaymentPlanKey.BlazeTeam && (
        <>
          <p className={Styles.featureDetails}>
            <span>
              Everything in <strong>Pro</strong>, plus:
            </span>
          </p>
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>
              {usageBasedPricingEnabled ? (
                <>
                  {kFormatter(plan.additionals?.seats?.plan_limit)} seats (+
                  {currencyFormatter(plan.additionals?.seats?.[priceKey])} per additional seat)
                </>
              ) : (
                <>3 seats (+${additionalSeatPrice} per additional seat)</>
              )}
            </p>
          </div>
          {usageBasedPricingEnabled && (
            <div className={Styles.featureDetails}>
              <Check />
              <p className={Styles.featureText}>
                Unlimited AI Words{' '}
                <Tooltip label="Unlimited plans are subject to our Terms">
                  <span>ⓘ</span>
                </Tooltip>
              </p>
            </div>
          )}
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>6 Brand Voices</p>
          </div>
          {usageBasedPricingEnabled && (
            <div className={Styles.featureDetails}>
              <Check />
              <p className={Styles.featureText}>
                {kFormatter(plan.additionals?.plagiarism_checks?.plan_limit)} Plagiarism Checks, <br />
                then {currencyFormatter(plan.additionals?.plagiarism_checks?.[priceKey])} per check
              </p>
            </div>
          )}
          <div className={Styles.featureDetails}>
            <Check />
            <div>
              <p className={Styles.featureText}>Real-time Collaboration,</p>
              <p className={Styles.featureText}>Comments, & Tasks</p>
            </div>
          </div>
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>Content approval tools</p>
          </div>
        </>
      )}
      {plan.key === PaymentPlanKey.BlazeAgency && (
        <>
          <p className={Styles.featureDetails}>
            <span>
              Everything in <strong>Startup</strong>, plus:
            </span>
          </p>
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>
              {usageBasedPricingEnabled ? (
                <>
                  {kFormatter(plan.additionals?.seats?.plan_limit)} seats (+
                  {currencyFormatter(plan.additionals?.seats?.[priceKey])} per additional seat)
                </>
              ) : (
                <>5 seats (+${additionalSeatPrice} per additional seat)</>
              )}
            </p>
          </div>
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>12 Brand Voices</p>
          </div>
          {usageBasedPricingEnabled && (
            <div className={Styles.featureDetails}>
              <Check />
              <p className={Styles.featureText}>
                {kFormatter(plan.additionals?.plagiarism_checks?.plan_limit)} Plagiarism Checks, <br />
                then {currencyFormatter(plan.additionals?.plagiarism_checks?.[priceKey])} per check
              </p>
            </div>
          )}
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>Dedicated onboarding & workspace setup</p>
          </div>
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>Multi-workspace volume discounts</p>
          </div>
        </>
      )}
      {plan.key === PaymentPlanKey.BlazeBusiness && (
        <>
          <p className={Styles.featureDetails}>
            <span>
              Everything in <strong>Team</strong>, plus:
            </span>
          </p>
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>Includes 20+ users</p>
          </div>
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>SSO/SAML Login</p>
          </div>
          <div className={Styles.featureDetails}>
            <Check />
            <div>
              <p className={Styles.featureText}>Custom AI templates tailored to</p>
              <p className={Styles.featureText}>your business</p>
            </div>
          </div>
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>Dedicated Account Manager</p>
          </div>
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>Tailored Training Sessions</p>
          </div>
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>Workflow Setup</p>
          </div>
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>User Provisioning via SCIM</p>
          </div>
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>Multiple Workspaces</p>
          </div>
          <div className={Styles.featureDetails}>
            <Check />
            <p className={Styles.featureText}>API Access</p>
          </div>
        </>
      )}
    </div>
  )
}
